import React, { useState, useEffect } from 'react';
import { 
  FiTrendingUp, 
  FiTrendingDown, 
  FiDollarSign, 
  FiCalendar, 
  FiBarChart2,
  FiShoppingBag,
  FiRepeat
} from 'react-icons/fi';
import axios from 'axios';
import './reports.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SalesProfitReport = () => {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-indexed

  const [reportData, setReportData] = useState({
    monthlySales: null,
    yearlySales: null,
    yearlyComparison: null,
    monthlyProfit: null,
    yearlyProfit: null,
    purchases: null,
    loading: true,
    errors: {} // per-section errors instead of one global error
  });

  useEffect(() => {
    const fetchReportData = async () => {
      setReportData(prev => ({ ...prev, loading: true, errors: {} }));

      const [
        monthlySalesRes,
        yearlySalesRes,
        yearlyComparisonRes,
        profitRes,
        purchasesRes
      ] = await Promise.allSettled([
        axios.get('/sales/stats/monthly'),
        axios.get('/sales/stats/yearly'),
        axios.get('/sales/stats/yearly-comparison'),
        axios.get('/sales/stats/profit', { params: { year: selectedYear, month: selectedMonth } }),
        axios.get('/items/stats/purchases')
      ]);

      const errors = {};
      const getData = (result, key, label) => {
        if (result.status === 'fulfilled') return result.value.data;
        errors[key] = `Failed to load ${label}`;
        console.error(`Error loading ${label}:`, result.reason);
        return null;
      };

      const monthlySales = getData(monthlySalesRes, 'monthlySales', 'monthly sales');
      const yearlySales = getData(yearlySalesRes, 'yearlySales', 'yearly sales');
      const yearlyComparison = getData(yearlyComparisonRes, 'yearlyComparison', 'yearly comparison');
      const profit = getData(profitRes, 'profit', 'profit data');
      const purchases = getData(purchasesRes, 'purchases', 'purchases data');

      setReportData({
        monthlySales,
        yearlySales,
        yearlyComparison,
        monthlyProfit: profit?.monthly || null,
        yearlyProfit: profit?.yearly || null,
        purchases,
        loading: false,
        errors
      });
    };

    fetchReportData();
  }, [selectedYear, selectedMonth]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined || typeof value !== 'number') {
      return 'N/A';
    }
    const numValue = Number(value);
    if (isNaN(numValue) || !isFinite(numValue)) {
      return 'N/A';
    }
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`;
  };

  // Safe division helper — avoids NaN / -Infinity / +Infinity ever reaching the UI
  const safeDivide = (numerator, denominator) => {
    if (!denominator || denominator === 0) return 0;
    const result = (numerator / denominator) * 100;
    return isFinite(result) ? result : 0;
  };

  const getTrendIcon = (value) => {
    if (value === null || value === undefined || isNaN(value)) return null;
    return value >= 0 ? (
      <FiTrendingUp className="trend-up" />
    ) : (
      <FiTrendingDown className="trend-down" />
    );
  };

  if (reportData.loading) {
    return <div className="loading">Loading report data...</div>;
  }

  // Find purchases entry matching the selected year/month for the cash-flow card
  const selectedMonthlyPurchase = reportData.purchases?.monthly?.find(
    p => p.year === selectedYear && p.month === selectedMonth
  );
  const selectedYearlyPurchase = reportData.purchases?.yearly?.find(
    p => p.year === selectedYear
  );

  // Latest available entries, used as fallback labels if no exact match for selected period
  const latestMonthlyPurchase = reportData.purchases?.monthly?.[0];
  const latestYearlyPurchase = reportData.purchases?.yearly?.[0];

  const monthlySalesForCashflow = reportData.monthlyProfit?.total_sales || 0;
  const monthlyPurchaseForCashflow = Number(selectedMonthlyPurchase?.total_purchase) || 0;
  const monthlyCashFlow = monthlySalesForCashflow - monthlyPurchaseForCashflow;

  const yearlySalesForCashflow = reportData.yearlyProfit?.yearly_totals?.total_sales || 0;
  const yearlyPurchaseForCashflow = Number(selectedYearlyPurchase?.total_purchase) || 0;
  const yearlyCashFlow = yearlySalesForCashflow - yearlyPurchaseForCashflow;

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) {
    yearOptions.push(y);
  }

  return (
    <div className="reports-container">
      <div className="reports-header-row">
        <h1 className="reports-title">
          <FiBarChart2 /> Sales, Profit & Purchases Report
        </h1>

        <div className="report-date-filter">
          <label htmlFor="reportMonth">
            <FiCalendar /> Period
          </label>
          <select
            id="reportMonth"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {MONTH_NAMES.map((name, idx) => (
              <option key={idx + 1} value={idx + 1}>{name}</option>
            ))}
          </select>
          <select
            id="reportYear"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {Object.keys(reportData.errors).length > 0 && (
        <div className="report-errors-banner">
          Some sections couldn't be loaded: {Object.values(reportData.errors).join(', ')}
        </div>
      )}

      <div className="report-grid">
        {/* Current Month Sales (always "current month" — endpoint doesn't accept a date param) */}
        {reportData.monthlySales && (
          <div className="report-card">
            <div className="card-header">
              <FiShoppingBag className="card-icon" />
              <h3>Current Month Sales</h3>
            </div>
            <div className="card-content">
              <p className="card-value">
                {formatCurrency(reportData.monthlySales?.current_sales)}
              </p>
              <div className="comparison">
                {getTrendIcon(reportData.monthlySales?.growth_percentage)}
                <span className={reportData.monthlySales?.growth_percentage >= 0 ? 'positive' : 'negative'}>
                  {formatPercentage(reportData.monthlySales?.growth_percentage)} vs last month
                </span>
              </div>
            </div>
            <div className="card-footer">
              <span>Transactions: {reportData.monthlySales?.current_transactions ?? 'N/A'}</span>
            </div>
          </div>
        )}

        {/* Year-to-Date Sales (always current year — endpoint doesn't accept a year param) */}
        {reportData.yearlySales && (
          <div className="report-card">
            <div className="card-header">
              <FiShoppingBag className="card-icon" />
              <h3>Year-to-Date Sales</h3>
            </div>
            <div className="card-content">
              <p className="card-value">
                {formatCurrency(reportData.yearlySales?.yearly_total)}
              </p>
              <div className="comparison">
                {getTrendIcon(reportData.yearlyComparison?.growth_percentage)}
                <span className={reportData.yearlyComparison?.growth_percentage >= 0 ? 'positive' : 'negative'}>
                  {formatPercentage(reportData.yearlyComparison?.growth_percentage)} vs last year
                </span>
              </div>
            </div>
            <div className="card-footer">
              <span>Avg. Monthly: {formatCurrency(reportData.yearlySales?.average_monthly)}</span>
            </div>
          </div>
        )}

        {/* Monthly Profit — respects the selected month/year */}
        {reportData.monthlyProfit && (
          <div className="report-card">
            <div className="card-header">
              <FiDollarSign className="card-icon" />
              <h3>Profit — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}</h3>
            </div>
            <div className="card-content">
              <p className={`card-value ${
                reportData.monthlyProfit?.net_profit >= 0 ? 'positive' : 'negative'
              }`}>
                {formatCurrency(reportData.monthlyProfit?.net_profit)}
              </p>
              <div className="profit-breakdown">
                <p>Sales: {formatCurrency(reportData.monthlyProfit?.total_sales)}</p>
                <p>Cost: {formatCurrency(reportData.monthlyProfit?.total_cost)}</p>
                <p>Expenses: {formatCurrency(reportData.monthlyProfit?.total_expenses)}</p>
              </div>
            </div>
            <div className="card-footer">
              <span>
                Margin: {formatPercentage(
                  safeDivide(reportData.monthlyProfit?.net_profit, reportData.monthlyProfit?.total_sales)
                )}
              </span>
            </div>
          </div>
        )}

        {/* Yearly Profit — respects the selected year */}
        {reportData.yearlyProfit?.yearly_totals && (
          <div className="report-card">
            <div className="card-header">
              <FiDollarSign className="card-icon" />
              <h3>Profit — {selectedYear}</h3>
            </div>
            <div className="card-content">
              <p className={`card-value ${
                reportData.yearlyProfit?.yearly_totals?.net_profit >= 0 ? 'positive' : 'negative'
              }`}>
                {formatCurrency(reportData.yearlyProfit?.yearly_totals?.net_profit)}
              </p>
              <div className="profit-breakdown">
                <p>Sales: {formatCurrency(reportData.yearlyProfit?.yearly_totals?.total_sales)}</p>
                <p>Cost: {formatCurrency(reportData.yearlyProfit?.yearly_totals?.total_cost)}</p>
                <p>Expenses: {formatCurrency(reportData.yearlyProfit?.yearly_totals?.total_expenses)}</p>
              </div>
            </div>
            <div className="card-footer">
              <span>
                Margin: {formatPercentage(
                  safeDivide(
                    reportData.yearlyProfit?.yearly_totals?.net_profit,
                    reportData.yearlyProfit?.yearly_totals?.total_sales
                  )
                )}
              </span>
            </div>
          </div>
        )}

        {/* Monthly Purchases vs Sales — cash flow, explicitly NOT profit */}
        {reportData.purchases && (
          <div className="report-card cashflow-card">
            <div className="card-header">
              <FiRepeat className="card-icon" />
              <h3>Cash Flow — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}</h3>
            </div>
            <div className="card-content">
              <p className={`card-value ${monthlyCashFlow >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(monthlyCashFlow)}
              </p>
              <div className="profit-breakdown">
                <p>Sales In: {formatCurrency(monthlySalesForCashflow)}</p>
                <p>Purchases Out: {formatCurrency(monthlyPurchaseForCashflow)}</p>
              </div>
            </div>
            <div className="card-footer">
              <span>Sales revenue minus inventory purchases — not the same as profit</span>
            </div>
          </div>
        )}

        {/* Yearly Purchases vs Sales — cash flow */}
        {reportData.purchases && (
          <div className="report-card cashflow-card">
            <div className="card-header">
              <FiRepeat className="card-icon" />
              <h3>Cash Flow — {selectedYear}</h3>
            </div>
            <div className="card-content">
              <p className={`card-value ${yearlyCashFlow >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(yearlyCashFlow)}
              </p>
              <div className="profit-breakdown">
                <p>Sales In: {formatCurrency(yearlySalesForCashflow)}</p>
                <p>Purchases Out: {formatCurrency(yearlyPurchaseForCashflow)}</p>
              </div>
            </div>
            <div className="card-footer">
              <span>Sales revenue minus inventory purchases — not the same as profit</span>
            </div>
          </div>
        )}

        {/* Monthly Sales Breakdown */}
        {reportData.yearlySales?.monthly_breakdown && (
          <div className="report-chart">
            <h3><FiCalendar /> Monthly Sales Breakdown ({reportData.yearlySales?.current_year})</h3>
            <div className="chart-container">
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Sales</th>
                    <th>Transactions</th>
                    <th>% of Year</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.yearlySales.monthly_breakdown.map(month => (
                    <tr key={month.month_year}>
                      <td>{month.month_name}</td>
                      <td>{formatCurrency(month.total_sales)}</td>
                      <td>{month.total_transactions}</td>
                      <td>
                        {formatPercentage(
                          safeDivide(month.total_sales, reportData.yearlySales.yearly_total)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Yearly Comparison */}
        {reportData.yearlyComparison && (
          <div className="report-chart">
            <h3><FiCalendar /> Yearly Comparison</h3>
            <div className="chart-container">
              <div className="comparison-grid">
                <div className="comparison-item">
                  <h4>{reportData.yearlyComparison?.current_year?.year || new Date().getFullYear()}</h4>
                  <p className="value">{formatCurrency(reportData.yearlyComparison?.current_year?.total_sales)}</p>
                  <p>{reportData.yearlyComparison?.current_year?.total_transactions ?? 0} transactions</p>
                </div>
                <div className="comparison-difference">
                  <div className={`difference ${reportData.yearlyComparison?.growth_percentage >= 0 ? 'positive' : 'negative'}`}>
                    {getTrendIcon(reportData.yearlyComparison?.growth_percentage)}
                    {formatPercentage(reportData.yearlyComparison?.growth_percentage)}
                    <div>{formatCurrency(reportData.yearlyComparison?.growth_amount)}</div>
                  </div>
                </div>
                <div className="comparison-item">
                  <h4>{reportData.yearlyComparison?.previous_year?.year || new Date().getFullYear() - 1}</h4>
                  <p className="value">{formatCurrency(reportData.yearlyComparison?.previous_year?.total_sales)}</p>
                  <p>{reportData.yearlyComparison?.previous_year?.total_transactions ?? 0} transactions</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesProfitReport;